import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Building2,
  LayoutDashboard,
  Table2,
  BarChart3,
  Send,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  Plus,
  ShieldAlert,
  Radio,
  FolderKanban,
  User as UserIcon,
  Lock,
  CreditCard,
  Activity,
  Server,
  Loader2,
  Layers,
  ArrowLeft,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNavigation } from '../../hooks/useNavigation';
import { OrganizationSelector } from './OrganizationSelector';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface SidebarProps {
  onNewTableModal?: () => void;
  onNewProjectModal?: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onNewTableModal,
  onNewProjectModal,
  mobileOpen = false,
  onCloseMobile,
}) => {
  const PINNED_STORAGE_KEY = 'ericius_sidebar_pinned';

  const [isPinned, setIsPinned] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(PINNED_STORAGE_KEY);
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  const [isHovered, setIsHovered] = useState(false);
  const [isOrgSelectorOpen, setIsOrgSelectorOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const togglePinned = () => {
    setIsPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PINNED_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Auto-close mobile drawer ONLY when route actually changes
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      prevPathname.current = location.pathname;
      onCloseMobile?.();
      if (!isPinned) {
        setIsHovered(false);
      }
    }
  }, [location.pathname, onCloseMobile, isPinned]);

  // Handle Escape key to close mobile drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) {
        onCloseMobile?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen, onCloseMobile]);
  const [createProjOpen, setCreateProjOpen] = useState(false);
  const [newProjName, setNewProjName] = useState('');
  const [isCreatingProj, setIsCreatingProj] = useState(false);
  const {
    tables,
    currentTable,
    setCurrentTable,
    currentOrg,
    projects,
    organizations,
    currentProject,
    setCurrentProject,
    createProject,
    createOrganization,
    switchOrganization,
    canAdmin,
    canEdit,
    role,
    user,
    isTablesLoading,
  } = useAuth();

  const [createOrgOpen, setCreateOrgOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const canManageOrg = Boolean(user?.is_superadmin || role === 'owner' || role === 'admin');

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    setIsCreatingOrg(true);
    try {
      const res = await createOrganization(newOrgName.trim());
      showToast({
        type: 'success',
        title: 'Organization Created',
        message: `Organization "${res.organization.name}" created.`,
      });
      setNewOrgName('');
      setCreateOrgOpen(false);
      navigate(`/organization/${res.organization.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Organization',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingOrg(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjName.trim() || !currentOrg?.id) return;

    setIsCreatingProj(true);
    try {
      const p = await createProject(currentOrg.id, newProjName.trim());
      showToast({
        type: 'success',
        title: 'Project Created',
        message: `Project "${p.name}" created under ${currentOrg.name}.`,
      });
      setNewProjName('');
      setCreateProjOpen(false);
      navigate(`/organization/${currentOrg.id}/projects/${p.id}`);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Create Project',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsCreatingProj(false);
    }
  };

  const {
    orgId,
    projId,
    tableId,
    isHomeMode,
    isAdminMode,
    isAccountMode,
    isOrgMode,
    isProjectScope,
    isTableScope,
    orgNavItems,
    projectNavItems,
    tableNavItems,
    accountNavItems,
    adminNavItems,
  } = useNavigation();

  const activeProject = (projId ? projects.find((p) => p.id === projId) : null) || currentProject;
  const activeTable = (tableId ? tables.find((t) => t.id === tableId) : null) || currentTable;

  const isEffectiveOpen =
    mobileOpen || isPinned || isHovered || isOrgSelectorOpen || createProjOpen || createOrgOpen;
  const collapsed = !isEffectiveOpen;

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden transition-opacity duration-200"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      {/* Desktop Layout Spacer (Preserves document flow space so unpinned hover overlay causes ZERO layout shift) */}
      <div
        className={`hidden md:block h-screen flex-shrink-0 transition-[width] duration-200 ease-in-out ${
          isPinned ? 'w-[16.25rem]' : 'w-14'
        }`}
        aria-hidden="true"
      />

      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed inset-y-0 right-0 md:left-0 md:right-auto z-50 h-screen bg-white dark:bg-[#2e2f3b] bg-cf-nav border-l md:border-r md:border-l-0 border-cf-border flex flex-col justify-between overflow-x-hidden transition-transform duration-200 ease-in-out md:transition-[width,box-shadow] flex-shrink-0 ${
          mobileOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-full md:translate-x-0'
        } ${
          isEffectiveOpen ? 'w-[16.25rem]' : 'w-[16.25rem] md:w-14'
        } ${
          !isPinned && isEffectiveOpen ? 'md:z-40 md:shadow-2xl' : 'md:z-20'
        }`}
      >
      {/* Brand Header / Organization Selector */}
      <OrganizationSelector
        collapsed={collapsed}
        isPinned={isPinned}
        onTogglePin={togglePinned}
        mobileOpen={mobileOpen}
        onCloseMobile={onCloseMobile}
        onOpenCreateOrg={() => setCreateOrgOpen(true)}
        onDropdownOpenChange={setIsOrgSelectorOpen}
      />

      {/* Middle Nav Section */}
      <div className="flex-1 flex flex-col py-3 overflow-y-auto min-h-0">
        {isAdminMode ? (
          /* ================= PLATFORM ADMIN SCOPE ================= */
          <div className="px-2 space-y-1">
            {!collapsed && (
              <div className="px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-rose-400">
                Administration
              </div>
            )}
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={() =>
                    `flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors outline-none focus:outline-none ${
                      item.isActive
                        ? 'bg-cf-surfaceHover text-cf-textPrimary'
                        : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={15} className="flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ) : isAccountMode ? (
          /* ================= TENANT ACCOUNT SCOPE ================= */
          <div className="px-2 space-y-1">
            {accountNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={() =>
                    `flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors outline-none focus:outline-none ${
                      item.isActive
                        ? 'bg-cf-surfaceHover text-cf-textPrimary'
                        : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    <Icon size={15} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </div>
                  {!collapsed && item.badge !== undefined && (
                    <span className="text-xs text-cf-textMuted bg-cf-bg px-1.5 py-0.2 rounded border border-cf-border">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ) : isHomeMode ? (
          /* ================= GLOBAL HOME SCOPE ================= */
          <div className="px-2 flex-1 flex flex-col min-h-0">
            {/* Organizations List */}
            <div className="flex-1 flex flex-col min-h-0">
              {!collapsed && (
                <div className="flex items-center justify-between px-2.5 mb-1.5 flex-shrink-0">
                  <span className="text-xs font-semibold uppercase text-cf-textMuted tracking-wider">
                    Organizations ({organizations.length})
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setCreateOrgOpen(true)}
                    title="Create new organization"
                    aria-label="Create new organization"
                  >
                    <Plus size={13} />
                  </Button>
                </div>
              )}

              <div className="space-y-0.5 overflow-y-auto flex-1 min-h-0">
                {organizations.map((org) => (
                  <NavLink
                    key={org.id}
                    to={`/organization/${org.id}`}
                    onClick={() => switchOrganization(org)}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded text-xs transition-colors text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary"
                    title={collapsed ? org.name : undefined}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Building2 size={14} className="flex-shrink-0 text-cf-textMuted" />
                      {!collapsed && <span className="truncate">{org.name}</span>}
                    </div>
                    {!collapsed && (
                      <span className="text-xs text-cf-textMuted uppercase font-sans">
                        {org.plan_tier}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Account Settings Quick Links */}
            <div className="pt-2 mt-3 border-t border-cf-border/60 flex-shrink-0">
              {!collapsed && (
                <div className="px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-cf-textMuted">
                  Account
                </div>
              )}
              <NavLink
                to="/account/general"
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded text-xs text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary transition-colors"
                title={collapsed ? 'Profile' : undefined}
              >
                <UserIcon size={14} className="flex-shrink-0" />
                {!collapsed && <span>Profile & Settings</span>}
              </NavLink>
              <NavLink
                to="/account/billing"
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded text-xs text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary transition-colors"
                title={collapsed ? 'Billing & Plans' : undefined}
              >
                <CreditCard size={14} className="flex-shrink-0" />
                {!collapsed && <span>Billing & Plans</span>}
              </NavLink>
            </div>
          </div>
        ) : isOrgMode ? (
          /* ================= ORGANIZATION SCOPE ================= */
          <div className="px-2 flex-1 flex flex-col min-h-0">
            <div className="space-y-1 flex-shrink-0">
              {orgNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors outline-none focus:outline-none ${
                        isActive
                          ? 'bg-cf-surfaceHover text-cf-textPrimary'
                          : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                      }`
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon size={15} className="flex-shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </div>
                    {!collapsed && item.badge !== undefined && (
                      <span className="text-xs text-cf-textMuted bg-cf-bg px-1.5 py-0.2 rounded border border-cf-border">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>

            {/* Projects List */}
            <div className="pt-2 mt-3 border-t border-cf-border/60 flex-1 flex flex-col min-h-0">
              {!collapsed && (
                <div className="flex items-center justify-between px-2.5 mb-1.5 flex-shrink-0">
                  <span className="text-xs font-semibold uppercase text-cf-textMuted tracking-wider flex items-center gap-1.5">
                    <span>Projects ({projects.length})</span>
                  </span>
                  {canManageOrg && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={onNewProjectModal || (() => setCreateProjOpen(true))}
                      title="Create new project"
                      aria-label="Create new project"
                    >
                      <Plus size={13} />
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-0.5 overflow-y-auto flex-1 min-h-0">
                {projects.map((p) => {
                  const projectRoute = `/organization/${orgId}/projects/${p.id}`;
                  return (
                    <NavLink
                      key={p.id}
                      to={projectRoute}
                      onClick={() => setCurrentProject(p)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-1.5 rounded text-xs transition-colors outline-none focus:outline-none ${
                          isActive
                            ? 'bg-cf-surfaceHover text-cf-textPrimary font-medium'
                            : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                        }`
                      }
                      title={collapsed ? p.name : undefined}
                    >
                      <FolderKanban size={14} className="flex-shrink-0" />
                      {!collapsed && <span className="truncate">{p.name}</span>}
                    </NavLink>
                  );
                })}

                {projects.length === 0 && !collapsed && (
                  <div className="px-1 py-1">
                    {canManageOrg ? (
                      <Button
                        type="button"
                        variant="orange-subtle"
                        size="xs"
                        fullWidth
                        onClick={onNewProjectModal || (() => setCreateProjOpen(true))}
                        leftIcon={<Plus size={12} />}
                      >
                        Create Project
                      </Button>
                    ) : (
                      <div className="px-2 py-2 text-xs text-cf-textMuted italic">
                        No projects assigned
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : isTableScope ? (
          /* ================= DATASTORE SCOPE ================= */
          <div className="px-2 space-y-1">
            {/* Datastore Navigation Items */}
            {tableNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.end}
                  className={() =>
                    `flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors outline-none focus:outline-none ${
                      item.isActive
                        ? 'bg-cf-surfaceHover text-cf-textPrimary font-medium'
                        : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                    }`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={15} className="flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ) : (
          /* ================= PROJECT SCOPE ================= */
          <div className="px-2 flex-1 flex flex-col min-h-0">
            <div className="space-y-1 flex-shrink-0">
              {/* Project Navigation Items */}
              {projectNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors outline-none focus:outline-none ${
                        isActive
                          ? 'bg-cf-surfaceHover text-cf-textPrimary'
                          : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                      }`
                    }
                    title={collapsed ? item.label : undefined}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon size={15} className="flex-shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </div>
                    {!collapsed && item.badge !== undefined && (
                      <span className="text-xs text-cf-textMuted bg-cf-bg px-1.5 py-0.2 rounded border border-cf-border">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>

            {/* Datastore Tables List */}
            <div className="pt-2 mt-3 border-t border-cf-border/60 flex-1 flex flex-col min-h-0">
              {!collapsed && (
                <div className="flex items-center justify-between px-2.5 mb-1.5 flex-shrink-0">
                  <span className="text-xs font-semibold uppercase text-cf-textMuted tracking-wider flex items-center gap-1.5">
                    <span>Datastores ({tables.length})</span>
                    {isTablesLoading && <Loader2 size={11} className="animate-spin text-cf-orange" />}
                  </span>
                  {(canAdmin || canEdit) && onNewTableModal && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={onNewTableModal}
                      title="Create new datastore table"
                      aria-label="Create new datastore table"
                    >
                      <Plus size={13} />
                    </Button>
                  )}
                </div>
              )}

              <div className="space-y-0.5 overflow-y-auto flex-1 min-h-0">
                {tables.map((table) => {
                  const tableRoute = `/organization/${orgId}/projects/${projId}/tables/${table.id}`;
                  return (
                    <NavLink
                      key={table.id}
                      to={tableRoute}
                      onClick={() => setCurrentTable(table)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-1.5 rounded text-xs transition-colors outline-none focus:outline-none ${
                          isActive
                            ? 'bg-cf-surfaceHover text-cf-textPrimary font-medium'
                            : 'text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary'
                        }`
                      }
                      title={collapsed ? table.name : undefined}
                    >
                      <Table2 size={14} className="flex-shrink-0" />
                      {!collapsed && <span className="truncate">{table.name}</span>}
                    </NavLink>
                  );
                })}

                {tables.length === 0 && !collapsed && (canAdmin || canEdit) && onNewTableModal && (
                  <div className="px-1 py-1">
                    <Button
                      type="button"
                      variant="orange-subtle"
                      size="xs"
                      fullWidth
                      onClick={onNewTableModal}
                      leftIcon={<Plus size={12} />}
                    >
                      Create Datastore
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Footer Section */}
      <div
        className={`border-t border-cf-border flex flex-col gap-1 flex-shrink-0 p-2 ${
          isTableScope || isProjectScope ? 'flex' : 'hidden md:flex'
        }`}
      >
        {isTableScope ? (
          <Link
            to={
              orgId && projId
                ? `/organization/${orgId}/projects/${projId}`
                : projId
                ? `/projects/${projId}`
                : orgId
                ? `/organization/${orgId}`
                : '/'
            }
            onClick={onCloseMobile}
            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary transition-colors outline-none focus:outline-none group cursor-pointer"
            title={collapsed ? 'Back to project' : undefined}
          >
            <ArrowLeft
              size={14}
              className="flex-shrink-0 text-cf-textMuted group-hover:text-cf-textPrimary group-hover:-translate-x-0.5 transition-all"
            />
            {!collapsed && <span className="truncate">Back to project</span>}
          </Link>
        ) : isProjectScope ? (
          <Link
            to={orgId ? `/organization/${orgId}` : '/'}
            onClick={onCloseMobile}
            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-cf-textSecondary hover:bg-cf-surfaceHover hover:text-cf-textPrimary transition-colors outline-none focus:outline-none group cursor-pointer"
            title={collapsed ? 'Back to organization' : undefined}
          >
            <ArrowLeft
              size={14}
              className="flex-shrink-0 text-cf-textMuted group-hover:text-cf-textPrimary group-hover:-translate-x-0.5 transition-all"
            />
            {!collapsed && <span className="truncate">Back to organization</span>}
          </Link>
        ) : null}

        {/* Separator between back navigation and close/open sidebar */}
        {(isTableScope || isProjectScope) && (
          <div className="border-t border-cf-border my-0.5 hidden md:block" />
        )}

        {/* Bottom Pin/Collapse Toggle (Desktop only) */}
        <div className="hidden md:flex justify-end pt-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={togglePinned}
            className="w-full"
            title={isPinned ? 'Collapse sidebar (unpin)' : 'Pin sidebar (keep open)'}
            aria-label={isPinned ? 'Collapse sidebar' : 'Pin sidebar'}
          >
            {isPinned ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </Button>
        </div>
      </div>
      </aside>

      {/* Create Project Modal */}
      <Modal
        isOpen={createProjOpen}
        onClose={() => setCreateProjOpen(false)}
        title={`Create Project in ${currentOrg?.name || 'Organization'}`}
      >
        <form onSubmit={handleCreateProject} className="space-y-4">
          <Input
            label="Project Name"
            type="text"
            required
            size="sm"
            value={newProjName}
            onChange={(e) => setNewProjName(e.target.value)}
            placeholder="e.g. Ingestion Webhooks"
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-cf-border">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={() => setCreateProjOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="xs"
              isLoading={isCreatingProj}
              disabled={isCreatingProj || !newProjName.trim()}
              leftIcon={<Plus size={13} />}
            >
              Create
            </Button>
          </div>
        </form>
      </Modal>

      {/* Create Organization Modal */}
      <Modal
        isOpen={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        title="Create New Organization"
      >
        <form onSubmit={handleCreateOrg} className="space-y-4">
          <Input
            label="Organization Name"
            type="text"
            required
            size="sm"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="e.g. Acme Corp or Marketing Team"
          />
          <div className="flex justify-end gap-2 pt-2 border-t border-cf-border">
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={() => setCreateOrgOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="xs"
              isLoading={isCreatingOrg}
              disabled={isCreatingOrg || !newOrgName.trim()}
              leftIcon={<Plus size={13} />}
            >
              Create Organization
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};
